import React, { useEffect, useRef, useState } from 'react';
import { Platform, Pressable, Text, View, StyleSheet } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { usePlannedMealTitle } from '../plannedMealTitle';
import { Paprika } from '../../../../styles/theme';
import { ENTRY_META, EntryIcon } from '../entryMeta';
import { entryTitle, isEntryMeal } from '../../../../redux/mealEntry';
import { proposalsOf, slotHasAcceptedMeal } from '../../proposalState';
import type { PlannedMeal } from '../../../../redux/mealPlanSlice';
import type { RecipeIndexEntry } from '../../../../services/unifiedRecipeService';
import { RemoteImage } from '../../../shared/RemoteImage';
import { makeDragStart } from './dragSource';
import { COMPACT_ROW_HEIGHT } from './rowFocus';

interface Props {
  sk: number;
  /** The slot's primary meal (accepted dish or entry), if any. */
  meal?: PlannedMeal;
  /** Every meal of the slot, proposals included. */
  slotMeals: PlannedMeal[];
  recipeIndex: Record<string, RecipeIndexEntry>;
  isNext?: boolean;
  flash?: boolean;
  dragOver?: boolean;
  /** Opens this cell's row. */
  onOpen: () => void;
  /** An empty strip cell: open the row and start filling this slot. */
  onAdd: () => void;
}

const isWeb = Platform.OS === 'web';

function formatMinutes(m: number): string {
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)}h${m % 60 ? String(m % 60).padStart(2, '0') : ''}`;
}

/**
 * One cell of a closed row of the desktop week grid (see rowFocus.ts): what the
 * slot holds, in a 50px strip. Clicking it opens the row rather than the
 * recipe — the big card it opens into is where the recipe, Remplacer and the
 * votes live. An empty cell still adds in one click.
 */
export function SlotMiniCell({ sk, meal, slotMeals, recipeIndex, isNext, flash, dragOver, onOpen, onAdd }: Props) {
  const { t } = useTranslation();
  const recipe = meal && !isEntryMeal(meal) ? recipeIndex[meal.recipeId] ?? null : null;
  // Accepted dishes beside the primary — a side under a recipe, or under a note
  // or leftovers since #798. Proposals are not on the menu, so not counted.
  const extras = meal ? slotMeals.filter((m) => m.id !== meal.id && m.mode !== 'proposed').length : 0;

  if (meal && isEntryMeal(meal)) {
    const m = ENTRY_META[meal.entry!.type];
    return (
      <Strip sk={sk} draggable onPress={onOpen} style={[dragOver && styles.dragOver]}>
        <View style={[styles.thumb, styles.iconBox, { backgroundColor: m.tint }]}>
          <EntryIcon type={meal.entry!.type} size={16} />
        </View>
        <View style={styles.body}>
          <Text style={[styles.kicker, { color: m.color }]} numberOfLines={1}>{t(m.labelKey).toUpperCase()}</Text>
          <View style={styles.meta}>
            <Text style={[styles.entryTitle, styles.shrink]} numberOfLines={1}>
              {entryTitle(meal.entry!, (id) => recipeIndex[id]?.title, t)}
            </Text>
            {extras > 0 && <Text style={styles.extra}>+{extras}</Text>}
          </View>
        </View>
      </Strip>
    );
  }

  if (meal && recipe) {
    return (
      <Strip
        sk={sk}
        draggable
        onPress={onOpen}
        style={[isNext && styles.next, flash && styles.next, dragOver && styles.dragOver]}
      >
        <MiniRecipe recipe={recipe} extras={extras} isCooked={meal.status === 'cooked'} />
      </Strip>
    );
  }

  const proposals = proposalsOf(slotMeals);
  if (proposals.length > 0 && !slotHasAcceptedMeal(slotMeals)) {
    return (
      <Strip sk={sk} onPress={onOpen} style={[styles.proposals, dragOver && styles.dragOver]}>
        <Text style={styles.toSettle} numberOfLines={2}>
          {t('household.approval.toSettle', { count: proposals.length })}
        </Text>
      </Strip>
    );
  }

  return (
    <Pressable
      testID="slot-mini-empty"
      style={[styles.empty, dragOver && styles.emptyGlow]}
      onPress={onAdd}
      accessibilityRole="button"
      accessibilityLabel={t('thisWeek.add')}
    >
      <Text style={[styles.plus, dragOver && styles.plusGlow]}>+</Text>
    </Pressable>
  );
}

function MiniRecipe({ recipe, extras, isCooked }: { recipe: RecipeIndexEntry; extras: number; isCooked: boolean }) {
  const { t } = useTranslation();
  const title = usePlannedMealTitle(recipe, 'desktop-mini');
  const minutes = recipe.readyInMinutes && recipe.readyInMinutes > 0 ? formatMinutes(recipe.readyInMinutes) : null;
  return (
    <>
      <RemoteImage
        uri={recipe.image}
        variant="thumb"
        style={styles.thumb}
        resizeMode="cover"
        fallback={<View style={[styles.thumb, styles.thumbPlaceholder]} />}
      />
      <View style={styles.body}>
        <Text style={styles.title} numberOfLines={minutes || extras || isCooked ? 1 : 2} testID="planned-meal-title">
          {title}
        </Text>
        {!!(minutes || extras > 0 || isCooked) && (
          <View style={styles.meta}>
            {isCooked && <Feather name="check" size={10} color={Paprika.success} />}
            {isCooked && <Text style={styles.metaText}>{t('thisWeek.cooked')}</Text>}
            {!isCooked && !!minutes && <Text style={styles.metaText}>{minutes}</Text>}
            {extras > 0 && <Text style={styles.extra}>+{extras}</Text>}
          </View>
        )}
      </View>
    </>
  );
}

/** The strip itself: a pressable row, a drag source when it holds a dish or an entry. */
function Strip({ sk, draggable, onPress, style, children }: {
  sk: number;
  draggable?: boolean;
  onPress: () => void;
  style?: any;
  children: React.ReactNode;
}) {
  const ref = useRef<any>(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (!isWeb || !draggable) return;
    const el = ref.current as HTMLElement | null;
    if (!el) return;
    const handleDragStart = makeDragStart(sk, setIsDragging);
    const handleDragEnd = () => setIsDragging(false);
    el.setAttribute('draggable', 'true');
    el.addEventListener('dragstart', handleDragStart);
    el.addEventListener('dragend', handleDragEnd);
    return () => {
      el.removeAttribute('draggable');
      el.removeEventListener('dragstart', handleDragStart);
      el.removeEventListener('dragend', handleDragEnd);
    };
  }, [sk, draggable]);

  return (
    <Pressable
      ref={ref}
      testID="slot-mini"
      style={[styles.strip, style, isDragging && styles.dragging]}
      onPress={onPress}
      accessibilityRole="button"
    >
      {children}
    </Pressable>
  );
}

const THUMB = COMPACT_ROW_HEIGHT - 10;

const styles = StyleSheet.create({
  strip: {
    height: COMPACT_ROW_HEIGHT,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 5,
    borderRadius: 9,
    backgroundColor: Paprika.surfaceLowest,
    overflow: 'hidden',
    shadowColor: 'rgba(82, 68, 60, 0.08)',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 1,
    shadowRadius: 5,
    elevation: 1,
    cursor: 'pointer',
  } as any,
  next: {
    borderWidth: 2,
    borderColor: Paprika.primary,
    padding: 3,
  },
  dragOver: {
    borderWidth: 1.5,
    borderColor: Paprika.primary,
    padding: 3.5,
  },
  dragging: {
    opacity: 0.4,
  },
  thumb: {
    width: THUMB,
    height: THUMB,
    borderRadius: 6,
    flexShrink: 0,
  },
  thumbPlaceholder: {
    backgroundColor: Paprika.bgSunk,
  },
  iconBox: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  body: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  title: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 12.5,
    lineHeight: 15,
    color: Paprika.textPrimary,
  },
  entryTitle: {
    fontFamily: 'Inter-Medium',
    fontSize: 12,
    color: Paprika.textPrimary,
  },
  kicker: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 9,
    letterSpacing: 0.6,
  },
  shrink: {
    flexShrink: 1,
  },
  meta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  metaText: {
    fontFamily: 'Inter-Regular',
    fontSize: 10.5,
    color: Paprika.textMuted,
  },
  extra: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 10,
    color: Paprika.primary,
  },
  proposals: {
    justifyContent: 'center',
    backgroundColor: Paprika.approvalIdeaTint,
    shadowOpacity: 0,
  },
  toSettle: {
    fontFamily: 'Inter-Medium',
    fontSize: 11,
    color: Paprika.approvalIdea,
    textAlign: 'center',
  },
  empty: {
    height: COMPACT_ROW_HEIGHT,
    borderWidth: 1.5,
    borderStyle: 'dashed',
    borderColor: Paprika.border,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0.75,
  },
  emptyGlow: {
    borderColor: Paprika.brandSoft,
    backgroundColor: Paprika.brandTint,
    opacity: 1,
  },
  plus: {
    fontSize: 16,
    lineHeight: 18,
    color: Paprika.border,
  },
  plusGlow: {
    color: Paprika.primary,
  },
});
